import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Loader2, Gift } from "lucide-react";
import { BRAND } from "@/config/brand";

interface NewsletterFormProps {
  variant?: "default" | "compact" | "footer";
  showTitle?: boolean;
  showDescription?: boolean;
  className?: string;
}

export const NewsletterForm = ({
  variant = "default",
  showTitle = true,
  showDescription = true,
  className = "",
}: NewsletterFormProps) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !email.trim()) {
      toast({
        title: "Champs obligatoires",
        description: "Veuillez renseigner votre prénom et votre email.",
        variant: "destructive",
      });
      return;
    }

    if (!gdprConsent) {
      toast({
        title: "Consentement requis",
        description: "Veuillez accepter les conditions pour vous inscrire.",
        variant: "destructive",
      });
      return;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Email invalide",
        description: "Veuillez saisir une adresse email valide.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("newsletter-subscribe", {
        body: {
          firstName: firstName.trim(),
          email: email.trim().toLowerCase(),
          gdprConsent,
        },
      });

      if (error) throw error;

      if (data?.alreadySubscribed) {
        toast({
          title: "Déjà inscrit(e)",
          description: "Cette adresse email est déjà inscrite à la newsletter.",
        });
      } else {
        setIsSuccess(true);
        toast({
          title: "Inscription réussie !",
          description: "Vérifiez votre boîte mail pour confirmer votre inscription et recevoir votre cadeau.",
        });
        setFirstName("");
        setEmail("");
        setGdprConsent(false);
      }
    } catch (error: any) {
      console.error("Erreur inscription newsletter:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={`text-center py-6 ${className}`}>
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Gift className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-xl font-cinzel font-semibold text-primary mb-2">Merci pour votre inscription !</h3>
        <p className="text-sm text-muted-foreground font-montserrat">
          Un email de confirmation vous a été envoyé.
          <br />
          Confirmez votre inscription pour recevoir votre cadeau.
        </p>
      </div>
    );
  }

  const isCompact = variant === "compact" || variant === "footer";

  return (
    <div className={className}>
      {showTitle && (
        <h3
          className={`font-cinzel font-semibold text-primary mb-2 ${
            isCompact ? "text-lg" : "text-xl sm:text-2xl"
          }`}
        >
          Recevoir des conseils pour mieux comprendre votre terrain
        </h3>
      )}

      {showDescription && (
        <p className={`text-muted-foreground font-montserrat mb-6 ${isCompact ? "text-sm" : "text-base"}`}>
          Recevez ponctuellement des contenus éducatifs, des conseils saisonniers et les actualités de{" "}
          {BRAND.name.full}.
          <br />
          <span className="text-xs italic">Une newsletter sobre, respectueuse de votre rythme, sans spam.</span>
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className={`grid gap-4 ${isCompact ? "grid-cols-1" : "sm:grid-cols-2"}`}>
          <div className="space-y-2">
            <Label htmlFor="newsletter-firstname" className="text-sm font-montserrat">
              Prénom
            </Label>
            <Input
              id="newsletter-firstname"
              type="text"
              placeholder="Votre prénom"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="font-montserrat"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newsletter-email" className="text-sm font-montserrat">
              Email
            </Label>
            <Input
              id="newsletter-email"
              type="email"
              placeholder="votre@email.fr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="font-montserrat"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <Checkbox
            id="newsletter-consent"
            checked={gdprConsent}
            onCheckedChange={(checked) => setGdprConsent(checked === true)}
            disabled={isSubmitting}
            className="mt-1"
          />
          <Label
            htmlFor="newsletter-consent"
            className="text-xs text-muted-foreground font-montserrat leading-relaxed cursor-pointer"
          >
            En vous inscrivant, vous acceptez de recevoir des informations et conseils bien-être de{" "}
            {BRAND.name.full}. Vous pouvez vous désinscrire à tout moment.
          </Label>
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-gradient-primary text-primary-foreground hover:opacity-90 font-montserrat"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Inscription...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4 mr-2" />
              S'inscrire
            </>
          )}
        </Button>
      </form>
    </div>
  );
};
